import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { registerRecoveryHandler, clearAuthRecovery } from '@/lib/authRecovery';
import type { Permissions } from '@/lib/rbac';
import type { Session, User } from '@supabase/supabase-js';

// Perfil administrativo derivado de `app_users` + `roles`.
export interface AdminProfile {
  fullName: string;
  role: string;
  permissions: Permissions;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: AdminProfile | null;
  loading: boolean;
  profileLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  // Resuelve el nombre real, el rol y sus permisos (JSONB) consultando
  // `app_users` (por auth.uid) y, si hay role_id, el rol en `roles`.
  async function loadProfile(userId: string, email?: string) {
    if (!isSupabaseConfigured || !supabase) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    setProfileLoading(true);

    let appUser: { full_name: string | null; role_id: string | null } | null = null;
    let userError: { message: string } | null = null;

    const byId = await supabase
      .from('app_users')
      .select('full_name, role_id')
      .eq('id', userId)
      .maybeSingle();

    if (byId.error || !byId.data) {
      // Fallback seguro por email si el id (auth.uid) no coincide.
      if (email) {
        const byEmail = await supabase
          .from('app_users')
          .select('full_name, role_id')
          .eq('email', email)
          .maybeSingle();
        appUser = (byEmail.data as { full_name: string | null; role_id: string | null } | null) ?? null;
        if (byEmail.error) userError = { message: byEmail.error.message };
      } else {
        userError = { message: byId.error ? byId.error.message : 'user_not_found' };
      }
    } else {
      appUser = byId.data as { full_name: string | null; role_id: string | null };
    }

    const row = appUser;

    if (userError || !row) {
      setProfile({ fullName: '', role: 'Sin rol', permissions: {} });
      setProfileLoading(false);
      return;
    }

    let roleName = 'Sin rol';
    let permissions: Permissions = {};
    if (row.role_id) {
      const { data: role, error: roleError } = await supabase
        .from('roles')
        .select('name, permissions')
        .eq('id', row.role_id)
        .maybeSingle();

      if (!roleError && role) {
        const r = role as { name: string | null; permissions: Permissions | null };
        roleName = r.name ?? 'Sin rol';
        permissions = r.permissions ?? {};
      }
    }

    setProfile({ fullName: row.full_name ?? '', role: roleName, permissions });
    setProfileLoading(false);
  }

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false);
      setProfileLoading(false);
      return;
    }

    // Registra el manejador que renueva la sesión cuando un error de token
    // (p. ej. "JWT issued at future") lo dispara. Si no puede renovar, aplica
    // un cierre limpio de sesión para evitar que la app quede en un estado roto.
    registerRecoveryHandler(async () => {
      if (!supabase) return false;
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data.session) {
          clearAuthRecovery();
          return true;
        }
      } catch {
        /* intenta el cierre limpio abajo */
      }
      try {
        await supabase.auth.signOut();
      } catch {
        /* ignore */
      }
      return false;
    });

    // 1. Restaura la sesión persistida al montar. No se bloquea el render en
    //    el perfil; se resuelve en paralelo (regla Supabase: setLoading(false)
    //    inmediatamente tras getSession()).
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
      if (data.session?.user) {
        clearAuthRecovery();
        loadProfile(data.session.user.id, data.session.user.email);
      } else {
        setProfileLoading(false);
      }
    });

    // 2. Escucha cambios de sesión en tiempo real. El callback es síncrono
    //    (sin async/await) para evitar deadlocks dentro del lock del SDK.
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        clearAuthRecovery();
        loadProfile(newSession.user.id, newSession.user.email);
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });

    return () => {
      registerRecoveryHandler(null);
      authListener.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signOut() {
    if (supabase) {
      await supabase.auth.signOut();
    }
    setSession(null);
    setUser(null);
    setProfile(null);
    setProfileLoading(false);
  }

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, profileLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return ctx;
}